import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  CloseButton,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Skeleton,
  Spacer,
  useDisclosure,
} from "@chakra-ui/react";
import { useFlags } from "flagsmith/react";
import Markdown from "markdown-to-jsx";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { RiArrowDownSLine, RiUploadLine } from "react-icons/ri";
import { useParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { MenuItemWithDescription } from "src/components/Menus/MenuItemWithDescription";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useSchoolAdminPermissions } from "src/hooks/useSchoolAdminPermissions";
import { GET_SCHOOLS_FOR_ENROLLMENT_PERIOD } from "src/scenes/orgAdmin/enrollmentPeriods/graphql/queries";
import {
  FormTemplateId,
  hasChanges,
} from "src/scenes/orgAdmin/formTemplates/types/common";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { GET_MESSAGE_TEMPLATE_TYPES } from "../graphql/queries";
import { ExportButton } from "./components/ExportButton";
import { ImportFromJSON } from "./components/ImportFromJSON";
import { ImportFromOtherDialog } from "./components/ImportFromOtherDialog";
import { ListMessageTemplatesForm } from "./components/ListMessageTemplatesForm";
import {
  checkResetComplete,
  ListMessageTemplateProvider,
  useListMessagesTemplateContext,
} from "./context";
import { formatInitialValues, formatMessageTemplates } from "./helpers";
import { ImportResult } from "./types";

export const ListMessageTemplates: FunctionComponent = () => {
  const { formTemplateId = "", enrollmentPeriodId = "" } = useParams();
  const flags = useFlags(["message-template-export-import"]);
  const { schoolFilter } = useSchoolAdminPermissions();
  const {
    isOpen: isImportDialogOpen,
    onOpen: onImportDialogOpen,
    onClose: onImportDialogClose,
  } = useDisclosure();

  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [showImportJSON, setShowImportJSON] = useState(false);

  const { remoteData, refetch: refetchMessageTemplates } = useRemoteDataQuery<
    GQL.GetMessageTemplateTypes,
    GQL.GetMessageTemplateTypesVariables
  >(GET_MESSAGE_TEMPLATE_TYPES, {
    variables: {
      formTemplateId: formTemplateId,
    },
    skip: !formTemplateId,
    fetchPolicy: "cache-and-network",
  });

  const { remoteData: schoolListData } = useRemoteDataQuery<
    GQL.GetSchoolsForEnrollmentPeriod,
    GQL.GetSchoolsForEnrollmentPeriodVariables
  >(GET_SCHOOLS_FOR_ENROLLMENT_PERIOD, {
    variables: {
      enrollment_period_id: enrollmentPeriodId,
      school_filter: schoolFilter(),
    },
    skip: !enrollmentPeriodId,
  });

  const onImportComplete = useCallback(
    async (result: ImportResult) => {
      setImportErrors(result.errors || []);
      await refetchMessageTemplates();
      setShowImportJSON(false);
    },
    [refetchMessageTemplates]
  );

  const dismissIndividualError = useCallback((errorIndex: number) => {
    setImportErrors((prevErrors) =>
      prevErrors.filter((_, index) => index !== errorIndex)
    );
  }, []);

  if (remoteData.isNotAsked()) {
    return null;
  }

  // Show import JSON component when state is true
  if (showImportJSON) {
    return (
      <ImportFromJSON
        onBack={() => setShowImportJSON(false)}
        onComplete={onImportComplete}
        formTemplateId={formTemplateId}
      />
    );
  }

  return (
    <GQLRemoteDataView
      remoteData={RD.toTuple(remoteData, schoolListData)}
      error={() => <GenericError />}
      loading={<Skeleton width="16rem" height="2.25rem" />}
    >
      {([data, schoolListData]) => {
        let rawMessageTemplates = data;
        /* TODO Start 2: remove this if some day we remove the form admin canceled template from database*/
        const formAdminCanceledRemoved =
          rawMessageTemplates.message_template_type.filter(
            (messageTemplateType) => {
              return (
                messageTemplateType.value !==
                GQL.message_template_type_enum.FormAdminCancelled
              );
            }
          );

        rawMessageTemplates = {
          ...data,
          message_template_type: formAdminCanceledRemoved,
        };
        /* TODO End 2 */

        const messageTemplates = formatMessageTemplates(
          rawMessageTemplates,
          formTemplateId,
          schoolListData.school
        );

        const initialValues = formatInitialValues(messageTemplates);

        const isRankingEnabled =
          !!data.form_template_by_pk?.sections[0]?.schools_ranking_section
            ?.ranking_enabled;

        const isOfferEnabled =
          !!data.form_template_by_pk?.lottery_offers_enabled;

        const isSchoolSpecificMessagesEnabled =
          !!data.form_template_by_pk?.school_specific_messages_enabled;

        return (
          <ListMessageTemplateProvider
            formTemplateId={formTemplateId}
            listMessagesInitialValues={initialValues}
          >
            <Flex direction="column" gap="4">
              {flags["message-template-export-import"].enabled && (
                <Flex justifyContent="flex-end" gap={4}>
                  <ImportFromOtherDialog
                    isOpen={isImportDialogOpen}
                    onClose={onImportDialogClose}
                    targetFormTemplateId={formTemplateId}
                    onImportComplete={onImportComplete}
                  />
                  <ImportExportMenu
                    formTemplateId={formTemplateId}
                    onImportFromJSON={() => setShowImportJSON(true)}
                    onImportFromOther={() => onImportDialogOpen()}
                    isRankingEnabled={isRankingEnabled}
                  />
                </Flex>
              )}

              {importErrors.length > 0 && (
                <Flex direction="column" gap="2">
                  {importErrors.map((error, index) => (
                    <Alert
                      key={`import-error-${index}`}
                      status="error"
                      rounded="md"
                      position="relative"
                    >
                      <AlertIcon />
                      <AlertDescription>
                        <Markdown>{error}</Markdown>
                      </AlertDescription>
                      <CloseButton
                        alignSelf="flex-end"
                        position="absolute"
                        top="0.5rem"
                        right="0.5rem"
                        onClick={() => dismissIndividualError(index)}
                      />
                    </Alert>
                  ))}
                </Flex>
              )}
              <Spacer />
              <ListMessageTemplatesForm
                messageTemplates={messageTemplates}
                isRankingEnabled={isRankingEnabled}
                isOfferEnabled={isOfferEnabled}
                isSchoolSpecificMessagesEnabled={
                  isSchoolSpecificMessagesEnabled
                }
                schoolList={schoolListData.school}
              />
            </Flex>
          </ListMessageTemplateProvider>
        );
      }}
    </GQLRemoteDataView>
  );
};

// Component that handles import/export logic with access to context
const ImportExportMenu: FunctionComponent<{
  formTemplateId: uuid;
  onImportFromJSON: () => void;
  onImportFromOther: () => void;
  isRankingEnabled: boolean;
}> = ({
  formTemplateId,
  onImportFromJSON,
  onImportFromOther,
  isRankingEnabled,
}) => {
  const { dispatch, state } = useListMessagesTemplateContext();
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Unsaved changes",
    body: "You have unsaved changes?",
    confirmButton: { label: "Yes" },
    cancelButton: { label: "No" },
  });

  const hasChangesResult = useMemo(
    () =>
      hasChanges(
        state?.get("listMessages")?.get(FormTemplateId(formTemplateId))
          ?.original,
        state?.get("listMessages")?.get(FormTemplateId(formTemplateId))?.draft
      ),
    [state, formTemplateId]
  );

  const handleImportFromJSON = useCallback(async () => {
    if (hasChangesResult) {
      const confirmed = await confirm({
        header: "Import from JSON",
        body: "You have unsaved changes. Would you like to discard the changes and import from JSON?",
        cancelButton: {
          label: "No, cancel",
        },
        confirmButton: { label: "Yes, import from JSON" },
      });
      if (confirmed) {
        // Reset the form state and wait for completion
        if (dispatch) {
          dispatch({
            type: "Reset",
            formTemplateId,
          });

          // Wait for reset to complete
          await checkResetComplete(state);
        }
        // Proceed with import after reset is complete
        onImportFromJSON();
      }
    } else {
      onImportFromJSON();
    }
  }, [
    confirm,
    dispatch,
    formTemplateId,
    hasChangesResult,
    onImportFromJSON,
    state,
  ]);

  const handleImportFromOther = useCallback(async () => {
    if (hasChangesResult) {
      const confirmed = await confirm({
        header: "Import from existing form",
        body: "You have unsaved changes. Would you like to discard the changes and import from an existing form?",
        cancelButton: {
          label: "No, cancel",
        },
        confirmButton: { label: "Yes, import from an existing form" },
      });
      if (confirmed) {
        // Reset the form state and wait for completion
        if (dispatch) {
          dispatch({
            type: "Reset",
            formTemplateId,
          });

          // Wait for reset to complete
          await checkResetComplete(state);
        }
        // Proceed with import after reset is complete
        onImportFromOther();
      }
    } else {
      onImportFromOther();
    }
  }, [
    confirm,
    dispatch,
    formTemplateId,
    hasChangesResult,
    onImportFromOther,
    state,
  ]);

  const confirmExport = useCallback(async () => {
    return await confirm({
      body: "You have unsaved changes. Would you like to save the changes and export JSON?",
      cancelButton: {
        label: "No, cancel",
      },
      confirmButton: { label: "Yes, save and export JSON" },
    });
  }, [confirm]);

  return (
    <>
      {confirmationDialog}
      <Menu>
        <MenuButton
          as={Button}
          leftIcon={<RiUploadLine />}
          rightIcon={<RiArrowDownSLine />}
          variant="outline"
        >
          Import
        </MenuButton>
        <MenuList>
          <MenuItemWithDescription
            title=".JSON file"
            description="Replace this form's messages from a file"
            onClick={handleImportFromJSON}
          />
          <MenuItemWithDescription
            onClick={handleImportFromOther}
            title="From existing form"
            description="Replace content from an existing form template"
          />
        </MenuList>
      </Menu>
      <ExportButton
        formTemplateId={formTemplateId}
        hasChanges={hasChangesResult}
        confirm={confirmExport}
        isRankingEnabled={isRankingEnabled}
      />
    </>
  );
};
